import { X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { PendingSourceDocument } from '@/service/api'
import { activeFacets, EMPTY_FILTERS, type TagMappingFilters } from './filters'

interface Props {
  tab: 'matched' | 'pending'
  filters: TagMappingFilters
  onChange: (filters: TagMappingFilters) => void
  /** The scoped board's name. Empty while it is still loading. */
  boardName: string
  /** Source sheets contributing pending tags, to name the sheet facet. */
  documents: PendingSourceDocument[]
}

/**
 * The active facets, shown OUTSIDE the Filter menu.
 *
 * The Filter button's count pill said "2" and nothing else: which two, and how
 * to drop one, were both inside a dropdown. A reviewer who saw an empty list
 * under "Filter 2" could not tell a restrictive filter from missing data
 * without reopening the menu (Tag Mapping review 2026-09-28, F20 / C3 / D1).
 * The Data Catalog already answers this with a chip row — one removable pill
 * per active facet and a Clear All at the row's edge (`CatalogFilterBar`) —
 * and this is the same row, over this page's facets.
 *
 * The board chip used to be the ONE facet drawn out here, because arriving
 * from a board applies it without the reader having asked. It keeps its
 * accessible name; it is now simply the first chip of the row.
 */
const TagMappingFilterChips = ({ tab, filters, onChange, boardName, documents }: Props) => {
  const { t } = useTranslation()
  const facets = activeFacets(filters, tab)
  if (facets.length === 0) return null

  const boardLabel = t('data_catalog:tag_mapping.filters.board', { defaultValue: 'Board' })

  /** What the chip says: the VALUE, in the words the menu used to pick it. */
  const labelFor = (facet: keyof TagMappingFilters): string => {
    switch (facet) {
      case 'boardId':
        return boardName || boardLabel
      case 'method':
        return filters.method === 'manual'
          ? t('data_catalog:tag_mapping.filters.manual', { defaultValue: 'Manually mapped' })
          : t('data_catalog:tag_mapping.filters.auto', { defaultValue: 'Automatically mapped' })
      case 'sourceType':
        return filters.sourceType === 'document'
          ? t('data_catalog:doc_types.document', { defaultValue: 'Document' })
          : t('data_catalog:doc_types.timeseries', { defaultValue: 'Time Series' })
      case 'confidence':
        return filters.confidence === 'strong'
          ? t('data_catalog:tag_mapping.filters.strong', { defaultValue: 'Strong (90%+)' })
          : filters.confidence === 'medium'
            ? t('data_catalog:tag_mapping.filters.medium', { defaultValue: 'Medium (50–89%)' })
            : t('data_catalog:tag_mapping.filters.weak', { defaultValue: 'Weak (under 50%)' })
      case 'source':
        return filters.source === 'sheet'
          ? t('data_catalog:tag_mapping.filters.source_drawings', { defaultValue: 'Drawings' })
          : t('data_catalog:tag_mapping.filters.source_documents', { defaultValue: 'Documents' })
      case 'documentId':
        return (
          documents.find((doc) => doc.document_id === filters.documentId)?.document_name ??
          t('data_catalog:tag_mapping.filters.source_sheet', { defaultValue: 'Source sheet' })
        )
    }
  }

  /** The × removes THIS facet and leaves the rest alone. */
  const clear = (facet: keyof TagMappingFilters) => onChange({ ...filters, [facet]: '' })

  return (
    <div
      className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 dark:bg-dark-accent/40"
      data-testid="tag-mapping-filter-chips"
    >
      <div className="flex flex-wrap items-center gap-2">
        {facets.map((facet) => {
          const label = labelFor(facet)
          return (
            <span
              key={facet}
              className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-border bg-background py-1 pe-1.5 ps-2.5 text-xs font-medium text-foreground"
            >
              <span className="truncate">{label}</span>
              <button
                type="button"
                onClick={() => clear(facet)}
                aria-label={
                  // The board keeps the name it had as a standalone button:
                  // arriving from a board is the one case the reader did not
                  // choose the filter, so the control says what it does.
                  facet === 'boardId'
                    ? t('data_catalog:tag_mapping.filters.clear_board', {
                        defaultValue: 'Show tags from all boards',
                      })
                    : t('data_catalog:filter.remove_chip', {
                        defaultValue: 'Remove {{label}} filter',
                        label,
                      })
                }
                className="grid size-4 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </span>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => onChange(EMPTY_FILTERS)}
        className="shrink-0 whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('data_catalog:filter.clear_all', { defaultValue: 'Clear All Filters' })}
      </button>
    </div>
  )
}

export default TagMappingFilterChips
